"use client";
import { twMerge } from "tailwind-merge";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps } from "../DataTable.types";
// Declared in a client-free module so `@texturehq/edges/server` can re-export them —
// see the note there. Imported here because the cell's own default swatch is one of them.
import { CATEGORY_NEUTRAL_COLOR } from "./categoryColors";
import { getCellContainerClasses, getSkeletonSize, resolveValue } from "./cellUtils";

export interface CategoryCellProps<T = any> extends CellComponentProps<T> {
  /**
   * The category label. The table passes the accessor's value; a resolver is also
   * accepted.
   *
   * Required rather than optional — `CellComponentProps.value` is required, and an
   * interface cannot relax that — so a standalone render passes `value={undefined}`
   * explicitly, the same way `RangeCell`'s callers do. `null` and `undefined` are in
   * the union because a row with no category is the common case, not an error.
   */
  value: string | ((row: T) => string | null) | null | undefined;
  /**
   * The swatch colour, as a CSS custom-property reference
   * (`var(--color-viz-categorical-3)`) — resolved by the CALLER, not here.
   *
   * Omitted ⇒ {@link CATEGORY_NEUTRAL_COLOR}. Deliberately not derived from `value`:
   * a cell sees one row, so any in-cell derivation is either "slot 1 for everything"
   * or a hash into twelve slots, and the second is a silent collision. The rule that
   * picks a slot — the twelve most frequent values in *this* rendering — is a fact
   * about every row, so only something holding every row can apply it.
   *
   * A `var(...)` reference rather than a resolved hex, for the same reason
   * `PercentBarCell` takes one: the browser resolves it per theme, so one stored
   * value is correct in light and dark, and a persisted board does not freeze
   * today's palette.
   */
  color?: string;
  /** Swatch shape. The label always renders beside it. */
  swatch?: "dot" | "square";
  /** Shown when the row has no category. No swatch is drawn beside it. */
  emptyText?: string;
  align?: "left" | "center" | "right";
  className?: string;
}

/**
 * CategoryCell
 *
 * A category swatch and its label: the value's identity gets a colour from the same
 * twelve-slot categorical ramp the charts and maps use, so a table under a breakdown
 * chart reads as the same data instead of making the reader re-match labels by eye.
 *
 * ## Swatch *and* label, never the swatch alone
 *
 * Colour is not a label — it fails for ~4% of readers outright, and for everyone at a
 * glance. The label is always rendered; the swatch is the second channel, the same
 * rule `DeviceStateCell` follows for its glyph.
 *
 * ## It colours by identity, not by meaning
 *
 * This is the whole boundary against `badge`/`status`: those colour by *semantics*
 * (five feedback variants — success is green because success is good), this colours by
 * *identity* (Tesla gets a stable slot because it is Tesla). Same visual family,
 * opposite rule. Do not add a `variant` here, and do not teach `badge` a ramp.
 *
 * ## It derives nothing
 *
 * `color` in, swatch out. See the prop's own note for why there is no fallback
 * derivation — an in-cell one is how silent palette cycling gets reintroduced.
 */
export function CategoryCell<T = any>({
  value,
  row,
  context,
  color,
  swatch = "dot",
  emptyText = "—",
  align = "left",
  className,
}: CategoryCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize("normal")} />;
  }

  const resolved = resolveValue(value, row);
  const label = resolved === null || resolved === undefined ? "" : String(resolved);

  // No swatch beside nothing: a coloured dot next to an em dash reads as a category
  // whose name failed to load, which is a different (and untrue) statement.
  if (label.length === 0) {
    return <div className={getCellContainerClasses(align, "text-text-secondary")}>{emptyText}</div>;
  }

  return (
    <div className={getCellContainerClasses(align, twMerge("gap-2", className))}>
      <span
        aria-hidden="true"
        className={twMerge("h-2 w-2 flex-shrink-0", swatch === "dot" ? "rounded-full" : "rounded-sm")}
        style={{ backgroundColor: color ?? CATEGORY_NEUTRAL_COLOR }}
      />
      <span className="truncate">{label}</span>
    </div>
  );
}
